import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import API_BASE from "../../config";
import { formatDate, formatCurrency } from "../../utils/format";
import {
  CheckCircleIcon,
  ClockIcon,
  StarIcon,
  ShieldCheckIcon,
  PhoneIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolid, StarIcon as StarSolid } from "@heroicons/react/24/solid";

export default function ClientQuotePage() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acceptedIndex, setAcceptedIndex] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [pendingCabinIndex, setPendingCabinIndex] = useState(null);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const res = await axios.get(`${API_BASE}/client/quote/${shareToken}`);
        setData(res.data);
        if (res.data.quote.status === "accepted") {
          setAcceptedIndex(res.data.quote.acceptedCabinIndex);
        }
      } catch (err) {
        setError(err.response?.status === 404 ? "Quote not found" : "Failed to load quote");
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [shareToken]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API_BASE}/client/quote/${shareToken}/accept`, {
        cabinIndex: pendingCabinIndex,
      });
      setAcceptedIndex(pendingCabinIndex);
      setShowAcceptConfirm(false);
      setData((prev) => ({
        ...prev,
        quote: { ...prev.quote, status: "accepted" },
      }));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to accept quote");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin h-10 w-10 border-3 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Oops!</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const { quote, agent } = data;
  const brandColor = agent?.brandColor || "#4F46E5";
  const isAccepted = quote.status === "accepted" || acceptedIndex !== null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Branded Header */}
      <header className="text-white" style={{ backgroundColor: brandColor }}>
        <div className="max-w-3xl mx-auto px-4 py-8 text-center">
          {agent?.logo ? (
            <img
              src={agent.logo}
              alt={agent.company || agent.name}
              className="max-h-12 mx-auto mb-4 object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <h1 className="text-2xl font-bold mb-1">{agent?.company || agent?.name || "Your Travel Agent"}</h1>
          )}
          {agent?.tagline && <p className="text-sm opacity-90 mb-4">{agent.tagline}</p>}
          <h2 className="text-xl font-semibold mt-2">Your Cruise Quote</h2>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 -mt-4">
        {/* Status banner */}
        {isAccepted && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <CheckCircleSolid className="h-6 w-6 text-green-500 flex-shrink-0" />
            <div>
              <p className="font-medium text-green-800">Quote accepted!</p>
              <p className="text-sm text-green-600">
                {agent?.name} will be in touch to finalize your booking.
              </p>
            </div>
          </div>
        )}

        {/* Personal message */}
        {quote.customMessage && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
            <div className="flex items-start gap-3">
              {agent?.agentPhoto ? (
                <img src={agent.agentPhoto} alt="" className="h-10 w-10 rounded-full object-cover flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                  style={{ backgroundColor: brandColor }}
                >
                  {agent?.name?.charAt(0) || "A"}
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">{agent?.name}</p>
                <p className="text-gray-700">{quote.customMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Cruise Details */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium uppercase tracking-wider mb-1" style={{ color: brandColor }}>
                  {quote.sailing.cruiseLine}
                </p>
                <h2 className="text-2xl font-bold text-gray-900">{quote.sailing.ship}</h2>
                <p className="text-gray-600 mt-1">{quote.sailing.product}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">{quote.sailing.nights} Nights</p>
                {quote.sailing.region && (
                  <p className="text-sm text-gray-500">{quote.sailing.region}</p>
                )}
              </div>
            </div>

            <div className="mt-4 flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <ClockIcon className="h-4 w-4" />
                <span>Departing {formatDate(quote.sailing.sailDate)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Cabin Options */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {quote.cabinSelections.length === 1 ? "Your Quote" : "Choose Your Cabin"}
          </h3>

          <div className="space-y-3">
            {quote.cabinSelections.map((cabin, idx) => {
              const isSelectedAccepted = acceptedIndex === idx;
              const canAccept = !isAccepted;

              return (
                <div
                  key={idx}
                  className={`relative p-5 rounded-xl border-2 transition-all ${
                    isSelectedAccepted
                      ? "border-green-500 bg-green-50"
                      : cabin.isRecommended
                      ? "border-2 bg-white"
                      : "border-gray-200 bg-white"
                  }`}
                  style={cabin.isRecommended && !isSelectedAccepted ? { borderColor: brandColor + "40", backgroundColor: brandColor + "08" } : {}}
                >
                  {cabin.isRecommended && !isSelectedAccepted && (
                    <div
                      className="absolute -top-3 left-4 px-3 py-0.5 rounded-full text-xs font-semibold text-white"
                      style={{ backgroundColor: brandColor }}
                    >
                      <StarSolid className="h-3 w-3 inline mr-1" /> Recommended
                    </div>
                  )}

                  {/* Cabin image */}
                  {cabin.cabinImage && (
                    <img
                      src={cabin.cabinImage}
                      alt={cabin.categoryName}
                      className="w-full h-40 rounded-lg object-cover mb-4"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}

                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <h4 className="text-lg font-semibold text-gray-900">{cabin.categoryName}</h4>
                      <p className="text-sm text-gray-500">Category {cabin.category}</p>
                      {/* Cabin details */}
                      {(cabin.cabinSize || cabin.cabinMaxOccupancy) && (
                        <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
                          {cabin.cabinSize > 0 && <span>{cabin.cabinSize} sq ft</span>}
                          {cabin.cabinMaxOccupancy > 0 && <span>Up to {cabin.cabinMaxOccupancy} guests</span>}
                        </div>
                      )}
                      {cabin.numGuests > 1 && (
                        <p className="text-sm text-gray-500 mt-0.5">{cabin.numGuests} guests</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(cabin.pricePerPerson, cabin.currency)}
                      </p>
                      <p className="text-sm text-gray-500">per person</p>
                      {cabin.numGuests > 1 && (
                        <p className="text-sm text-gray-600 mt-1">
                          Total: {formatCurrency(cabin.pricePerPerson * cabin.numGuests, cabin.currency)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Cabin description */}
                  {cabin.cabinDescription && (
                    <p className="text-sm text-gray-600 mt-3 leading-relaxed line-clamp-3">
                      {cabin.cabinDescription}
                    </p>
                  )}

                  {/* Cabin facilities */}
                  {cabin.cabinFacilities?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {cabin.cabinFacilities.slice(0, 8).map((f, fi) => (
                        <span key={fi} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">{f}</span>
                      ))}
                    </div>
                  )}

                  {isSelectedAccepted ? (
                    <div className="mt-3 flex items-center gap-2 text-green-700">
                      <CheckCircleSolid className="h-5 w-5" />
                      <span className="font-medium">Selected</span>
                    </div>
                  ) : canAccept ? (
                    <button
                      onClick={() => {
                        setPendingCabinIndex(idx);
                        setShowAcceptConfirm(true);
                      }}
                      className="mt-4 w-full py-3 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90"
                      style={{ backgroundColor: brandColor }}
                    >
                      Select This Option
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        {quote.notes && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Important Notes</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{quote.notes}</p>
          </div>
        )}

        {/* Pricing disclaimer */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
          <p className="text-xs text-gray-500 leading-relaxed">
            {quote.pricingDisclaimer || "Prices and availability are subject to change without notice and are not guaranteed until a deposit has been received and the booking is confirmed by the cruise line. All fares are per person based on double occupancy unless otherwise noted. Taxes, fees, and port charges may apply."}
          </p>
        </div>

        {/* Agent Contact Footer */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
          <div className="flex items-center gap-4">
            {agent?.agentPhoto ? (
              <img src={agent.agentPhoto} alt="" className="h-14 w-14 rounded-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <div
                className="h-14 w-14 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0"
                style={{ backgroundColor: brandColor }}
              >
                {agent?.name?.charAt(0) || "A"}
              </div>
            )}
            <div className="flex-1">
              <p className="font-semibold text-gray-900">{agent?.name}</p>
              {agent?.company && <p className="text-sm text-gray-600">{agent.company}</p>}
              <div className="flex items-center gap-4 mt-2">
                {agent?.email && (
                  <a
                    href={`mailto:${agent.email}`}
                    className="inline-flex items-center text-sm hover:underline"
                    style={{ color: brandColor }}
                  >
                    <EnvelopeIcon className="h-4 w-4 mr-1" /> {agent.email}
                  </a>
                )}
                {agent?.phone && (
                  <a
                    href={`tel:${agent.phone}`}
                    className="inline-flex items-center text-sm hover:underline"
                    style={{ color: brandColor }}
                  >
                    <PhoneIcon className="h-4 w-4 mr-1" /> {agent.phone}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 pb-8">
          Powered by QuickQuote
        </p>
      </div>

      {/* Accept Confirmation Modal */}
      {showAcceptConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Your Selection</h3>
            <p className="text-gray-600 mb-4">
              You're selecting the <strong>{quote.cabinSelections[pendingCabinIndex]?.categoryName}</strong> cabin
              at <strong>{formatCurrency(quote.cabinSelections[pendingCabinIndex]?.pricePerPerson, quote.cabinSelections[pendingCabinIndex]?.currency)}</strong> per person.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Your travel agent will contact you to finalize the booking and arrange payment.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowAcceptConfirm(false)}
                className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                disabled={accepting}
                className="flex-1 py-2.5 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: brandColor }}
              >
                {accepting ? "Confirming..." : "Confirm Selection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
